import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Trophy, Medal, Crown, Clock, Target, TrendingUp,
  Calendar, CalendarDays, Infinity, User
} from 'lucide-react';
import { useAuthStore } from '../lib/auth';
import api from '../lib/api';

const RANK_STYLES = {
  1: { icon: Crown, color: 'text-yellow-400', bg: 'bg-yellow-500/20' },
  2: { icon: Medal, color: 'text-gray-300', bg: 'bg-gray-500/20' },
  3: { icon: Medal, color: 'text-amber-600', bg: 'bg-amber-600/20' }
};

export default function BeatTheDataLeaderboard({ compact = false }) {
  const { user } = useAuthStore();
  const [timeframe, setTimeframe] = useState('all_time');
  const [leaderboard, setLeaderboard] = useState([]);
  const [totalParticipants, setTotalParticipants] = useState(0);
  const [userRank, setUserRank] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLeaderboard();
  }, [timeframe, user?.id]);

  const loadLeaderboard = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/v1/askalex/leaderboard`, {
        params: {
          timeframe,
          limit: compact ? 5 : 50,
          user_id: user?.id
        }
      });
      setLeaderboard(res.data.leaderboard || []);
      setTotalParticipants(res.data.total_participants || 0);
      setUserRank(res.data.user_rank);
    } catch (error) {
      console.error('Failed to load leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getAvatarUrl = (entry) => {
    const style = entry.avatar_style || 'bottts';
    const seed = entry.avatar_seed || entry.user_id;
    return `https://api.dicebear.com/7.x/${style}/svg?seed=${seed}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin w-8 h-8 border-2 border-[hsl(var(--brand-gold-500))] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (compact) {
    // Compact view for dashboard/sidebar
    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <Trophy className="w-4 h-4 text-[hsl(var(--brand-gold-500))]" />
            Beat the Data Leaders
          </h3>
          <Badge className="bg-[hsl(var(--brand-blue-800))] text-gray-300 text-xs">
            {totalParticipants} players
          </Badge>
        </div>
        
        {leaderboard.length > 0 ? (
          <div className="space-y-2">
            {leaderboard.slice(0, 5).map((entry, idx) => {
              const rankStyle = RANK_STYLES[entry.rank] || { icon: User, color: 'text-gray-400', bg: 'bg-gray-700/50' };
              const RankIcon = rankStyle.icon;
              
              return (
                <div 
                  key={entry.user_id}
                  className={`flex items-center gap-3 p-2 rounded-lg ${
                    entry.user_id === user?.id ? 'bg-[hsl(var(--brand-gold-500))]/10 border border-[hsl(var(--brand-gold-500))]/30' : rankStyle.bg
                  }`}
                >
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${entry.rank <= 3 ? '' : 'bg-gray-700'}`}>
                    {entry.rank <= 3 ? (
                      <RankIcon className={`w-4 h-4 ${rankStyle.color}`} />
                    ) : (
                      <span className="text-gray-400 text-xs font-bold">#{entry.rank}</span>
                    )}
                  </div>
                  <img 
                    src={getAvatarUrl(entry)} 
                    alt={entry.display_name}
                    className="w-6 h-6 rounded-full"
                  />
                  <span className="text-white text-sm flex-1 truncate">{entry.display_name}</span>
                  <span className="text-[hsl(var(--brand-gold-500))] font-bold text-sm">{entry.total_points}</span>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-4">
            <Trophy className="w-10 h-10 text-gray-600 mx-auto mb-2" />
            <p className="text-gray-400 text-sm">No players yet</p>
            <p className="text-gray-500 text-xs">Be the first to compete!</p>
          </div>
        )}
      </div>
    );
  }

  // Full leaderboard view
  return (
    <div>
      {/* Timeframe Tabs */}
      <Tabs value={timeframe} onValueChange={setTimeframe} className="mb-6">
        <TabsList className="bg-[hsl(var(--brand-blue-800))] border border-[hsl(var(--border))]">
          <TabsTrigger 
            value="daily"
            className="data-[state=active]:bg-[hsl(var(--brand-gold-500))] data-[state=active]:text-[hsl(var(--brand-blue-900))]"
          >
            <Calendar className="w-4 h-4 mr-2" />
            Daily
          </TabsTrigger>
          <TabsTrigger 
            value="weekly"
            className="data-[state=active]:bg-[hsl(var(--brand-gold-500))] data-[state=active]:text-[hsl(var(--brand-blue-900))]"
          >
            <CalendarDays className="w-4 h-4 mr-2" />
            Weekly
          </TabsTrigger>
          <TabsTrigger 
            value="all_time"
            className="data-[state=active]:bg-[hsl(var(--brand-gold-500))] data-[state=active]:text-[hsl(var(--brand-blue-900))]"
          >
            <Infinity className="w-4 h-4 mr-2" />
            All Time
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Stats Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Trophy className="w-5 h-5 text-[hsl(var(--brand-gold-500))]" />
          <span className="text-white font-semibold">
            {timeframe === 'daily' ? "Today's" : timeframe === 'weekly' ? "This Week's" : "All Time"} Leaders
          </span>
        </div>
        <Badge className="bg-[hsl(var(--secondary))] text-gray-300">
          {totalParticipants} Participants
        </Badge>
      </div>

      {/* User's Rank Card (if not in top) */}
      {userRank && !leaderboard.some(e => e.user_id === user?.id) && (
        <Card className="bg-[hsl(var(--brand-gold-500))]/10 border-[hsl(var(--brand-gold-500))]/30 mb-4">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-[hsl(var(--brand-gold-500))]/20 flex items-center justify-center">
                <span className="text-[hsl(var(--brand-gold-500))] font-bold">#{userRank}</span>
              </div>
              <div>
                <p className="text-white font-semibold">Your Ranking</p>
                <p className="text-gray-400 text-sm">Keep playing to climb!</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Leaderboard Table */}
      {leaderboard.length > 0 ? (
        <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
          <CardContent className="p-0">
            <div className="divide-y divide-[hsl(var(--border))]">
              {/* Header */}
              <div className="grid grid-cols-12 gap-4 px-4 py-3 text-gray-400 text-sm font-medium">
                <div className="col-span-1">Rank</div>
                <div className="col-span-4">Player</div>
                <div className="col-span-2 text-right">Points</div>
                <div className="col-span-2 text-right">Accuracy</div>
                <div className="col-span-2 text-right">Sessions</div>
                <div className="col-span-1 text-right">Best</div>
              </div>

              {/* Rows */}
              {leaderboard.map((entry) => {
                const isCurrentUser = entry.user_id === user?.id;
                const rankStyle = RANK_STYLES[entry.rank];
                const RankIcon = rankStyle?.icon;
                
                return (
                  <div 
                    key={entry.user_id}
                    className={`grid grid-cols-12 gap-4 px-4 py-3 items-center ${
                      isCurrentUser ? 'bg-[hsl(var(--brand-gold-500))]/10' : 'hover:bg-[hsl(var(--brand-blue-800))]/50'
                    }`}
                  >
                    {/* Rank */}
                    <div className="col-span-1">
                      {entry.rank <= 3 && RankIcon ? (
                        <div className={`w-8 h-8 rounded-full ${rankStyle.bg} flex items-center justify-center`}>
                          <RankIcon className={`w-5 h-5 ${rankStyle.color}`} />
                        </div>
                      ) : (
                        <span className="text-gray-400 font-bold">#{entry.rank}</span>
                      )}
                    </div>
                    
                    {/* Player */}
                    <div className="col-span-4 flex items-center gap-3">
                      <img 
                        src={getAvatarUrl(entry)} 
                        alt={entry.display_name}
                        className="w-8 h-8 rounded-full"
                      />
                      <span className={`font-medium truncate ${isCurrentUser ? 'text-[hsl(var(--brand-gold-500))]' : 'text-white'}`}>
                        {entry.display_name}
                        {isCurrentUser && <span className="text-xs ml-1">(You)</span>}
                      </span>
                    </div>
                    
                    {/* Points */}
                    <div className="col-span-2 text-right">
                      <span className="text-[hsl(var(--brand-gold-500))] font-bold">{entry.total_points.toLocaleString()}</span>
                    </div>
                    
                    {/* Accuracy */}
                    <div className="col-span-2 text-right">
                      <span className="text-emerald-400">{entry.accuracy}%</span>
                    </div>
                    
                    {/* Sessions */}
                    <div className="col-span-2 text-right">
                      <span className="text-gray-300">{entry.total_sessions}</span>
                    </div>
                    
                    {/* Best Accuracy */}
                    <div className="col-span-1 text-right">
                      <span className="text-cyan-400">{entry.best_accuracy}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
          <CardContent className="p-8 text-center">
            <Trophy className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-white font-semibold text-lg mb-2">No Players Yet</h3>
            <p className="text-gray-400 mb-4">
              {timeframe === 'daily' 
                ? "Be the first to compete today!" 
                : timeframe === 'weekly'
                ? "Be the first to compete this week!"
                : "Start a Beat the Data session to appear on the leaderboard!"
              }
            </p>
            <Button className="bg-[hsl(var(--brand-gold-500))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white text-[hsl(var(--brand-blue-900))]">
              Start Playing
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
